/**
 * Embedded entry: Reveal.js inside Lectura.
 * One script + slide HTML only. Lectura loads Reveal, inits it, then wires drawer/chat/notes/TTS/etc.
 *
 * Usage:
 *   <div id="lectura-root"><div class="slides"><section>...</section></div></div>
 *   <script src="dist/lectura-next-embedded.js"></script>
 *   <script>LecturaNextEmbedded.init(window.__LECTURA_NEXT_CONFIG__ || {});</script>
 */

import { createRevealAdapter } from './host-adapter';
import { init } from './orchestrator';
import { getConfig } from './core/config';
import { ensureSlidesWrap } from './shell';
import { registerTTS } from './features/tts';
import { showRemoteEmoteToast } from './features/remote/emote-toast';
import { applyRemoteV1CommandToReveal } from './services/remote/v1/reveal-bridge';
import type { RemoteV1CommandPayload } from './services/remote/v1/protocol';
import type { LecturaConfig, LecturaApi } from './core/types';
import {
  ensureRevealSummaryPrintPdfQuery,
  getRevealPrintPdfRevealOptions,
  injectRevealPrintPdfStyles,
  installRevealPrintHooks
} from './reveal-print';

const REVEAL_CDN = 'https://cdn.jsdelivr.net/npm/reveal.js@4.6.1';
const REVEAL_LOCAL = '/node_modules/reveal.js';
const COPYCODE_CDN = 'https://cdn.jsdelivr.net/npm/reveal.js-copycode@1.4.2';
const REVEAL_PLUGIN_PATHS = [
  '/plugin/notes/notes.js',
  '/plugin/highlight/highlight.js',
  '/plugin/search/search.js',
  '/plugin/zoom/zoom.js',
  '/plugin/math/math.js'
] as const;
const COPYCODE_JS_LOCAL = '/node_modules/reveal.js-copycode/plugin/copycode/copycode.js';
const COPYCODE_CSS_LOCAL = '/node_modules/reveal.js-copycode/plugin/copycode/copycode.css';
const DEFAULT_REVEAL_OPTIONS: Record<string, unknown> = {
  hash: true,
  help: false,
  keyboardCondition() {
    return !document.querySelector('.rf-settings-page.rf-settings-page-open');
  },
  keyboard: {
    83: null,
    84: () => {
      if (typeof (window as unknown as { LecturaNext?: LecturaApi }).LecturaNext !== 'undefined') {
        const ln = (window as unknown as { LecturaNext: LecturaApi }).LecturaNext;
        if (typeof ln.toggleTTS === 'function') ln.toggleTTS();
      }
    },
    191: null
  },
  controlsLayout: 'bottom-right',
  controlsBackArrows: 'visible',
  transition: 'slide',
  transitionSpeed: 'default',
  backgroundTransition: 'fade',
  slideNumber: 'c/t',
  showSlideNumber: 'all',
  width: 1280,
  height: 720,
  margin: 0.04,
  minScale: 0.2,
  maxScale: 2.0,
  center: true,
  display: 'block',
  pdfSeparateFragments: true,
  pdfMaxPagesPerSlide: 1,
  viewDistance: 3,
  mobileViewDistance: 2
};

function isLecturaEmbedPreviewMode(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    return (
      p.get('lecturaPreview') === '1' ||
      p.get('lecturaPreview') === 'true' ||
      p.get('remote') === '1'
    );
  } catch {
    return false;
  }
}

/** Buffered until `Reveal.slide` exists (parent may postMessage before embedded init finishes). */
let embedPreviewSlideTarget: [number, number, number] | null = null;

function tryApplyEmbedPreviewSlide(): void {
  if (embedPreviewSlideTarget == null) return;
  const w = window as unknown as {
    Reveal?: { slide?: (h: number, v: number, f: number) => void };
  };
  const R = w.Reveal;
  if (!R?.slide) return;
  const [h, v, f] = embedPreviewSlideTarget;
  R.slide(h, v, f);
}

function onEmbedPreviewParentMessage(e: MessageEvent): void {
  if (!document.documentElement.classList.contains('lectura-next-embed-preview')) return;
  if (e.source !== window.parent) return;
  const data = e.data;
  if (!data || typeof data !== 'object') return;

  const leg = data as {
    source?: string;
    action?: string;
    method?: string;
    args?: unknown;
    indexh?: number;
    indexv?: number;
    indexf?: number;
    emoji?: string;
    label?: string;
    ttl_ms?: number;
    scale?: number;
    command?: unknown;
  };

  if (leg.source === 'lectura-remote' && leg.action === 'remoteEmote') {
    const emoji = String(leg.emoji || '').trim();
    if (!emoji) return;
    const Rw = window as unknown as { Reveal?: { isLastSlide?: () => boolean } };
    const layout = Rw.Reveal?.isLastSlide?.() === true ? 'last_slide' : 'default';
    showRemoteEmoteToast(emoji, {
      label: typeof leg.label === 'string' ? leg.label : undefined,
      durationMs: typeof leg.ttl_ms === 'number' ? leg.ttl_ms : undefined,
      scale: typeof leg.scale === 'number' ? leg.scale : undefined,
      layout
    });
    return;
  }

  if (leg.source === 'lectura-remote' && leg.action === 'remoteCommand') {
    const cmd = leg.command;
    if (!cmd || typeof cmd !== 'object' || typeof (cmd as { type?: unknown }).type !== 'string') return;
    applyRemoteV1CommandToReveal(cmd as RemoteV1CommandPayload);
    return;
  }

  let h = 0;
  let v = 0;
  let f = 0;
  if (leg.method === 'slide' && Array.isArray(leg.args)) {
    const args = leg.args as number[];
    h = Number(args[0]) || 0;
    v = Number(args[1]) || 0;
    f = Number(args[2]) || 0;
  } else if (leg.source === 'lectura-remote' && leg.action === 'slide') {
    h = leg.indexh ?? 0;
    v = leg.indexv ?? 0;
    f = leg.indexf ?? 0;
  } else {
    return;
  }
  embedPreviewSlideTarget = [h, v, f];
  tryApplyEmbedPreviewSlide();
}

export interface EmbeddedUserConfig extends Partial<LecturaConfig> {
  container?: string;
  revealJsUrl?: string;
  revealCssUrl?: string;
  themeUrl?: string;
  reveal?: Record<string, unknown>;
}

function loadCSS(href: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = 'anonymous';
    link.onload = () => resolve();
    link.onerror = () => reject(new Error('Failed to load ' + href));
    document.head.appendChild(link);
  });
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load ' + src));
    document.body.appendChild(script);
  });
}

function getRevealPlugins(): unknown[] {
  const w = window as unknown as {
    RevealNotes?: unknown;
    RevealHighlight?: unknown;
    RevealSearch?: unknown;
    RevealZoom?: unknown;
    RevealMath?: unknown & { KaTeX?: unknown };
    CopyCode?: unknown;
  };
  const plugins: unknown[] = [];
  if (typeof w.RevealNotes !== 'undefined') plugins.push(w.RevealNotes);
  if (typeof w.RevealHighlight !== 'undefined') plugins.push(w.RevealHighlight);
  if (typeof w.RevealSearch !== 'undefined') plugins.push(w.RevealSearch);
  if (typeof w.RevealZoom !== 'undefined') plugins.push(w.RevealZoom);
  if (typeof w.RevealMath !== 'undefined') plugins.push(w.RevealMath.KaTeX || w.RevealMath);
  if (typeof w.CopyCode !== 'undefined') plugins.push(w.CopyCode);
  return plugins;
}

function ensureRevealSlides(root: Element): HTMLElement {
  let reveal = root.querySelector('.reveal');
  let slides = root.querySelector('.slides');
  if (reveal && slides && reveal.contains(slides)) return reveal as HTMLElement;
  if (!slides && reveal) {
    slides = reveal.querySelector('.slides') || document.createElement('div');
    if (!slides.classList.contains('slides')) {
      slides.className = 'slides';
      Array.from(reveal.children).forEach((c) => slides!.appendChild(c));
      reveal.appendChild(slides);
    }
    return reveal as HTMLElement;
  }
  if (slides && !reveal) {
    reveal = document.createElement('div');
    reveal.className = 'reveal';
    slides.parentNode!.insertBefore(reveal, slides);
    reveal.appendChild(slides);
    return reveal as HTMLElement;
  }
  reveal = document.createElement('div');
  reveal.className = 'reveal';
  slides = document.createElement('div');
  slides.className = 'slides';
  while (root.firstChild) slides.appendChild(root.firstChild);
  reveal.appendChild(slides);
  root.appendChild(reveal);
  return reveal as HTMLElement;
}

/**
 * Init embedded Lectura: load Reveal if needed, init Reveal, then init Lectura on ready.
 */
export function initEmbedded(userConfig: EmbeddedUserConfig = {}): Promise<LecturaApi> {
  const containerSelector = userConfig.container || '#lectura-root';
  const root = document.querySelector(containerSelector);
  if (!root) {
    return Promise.reject(new Error('Lectura embedded: container not found: ' + containerSelector));
  }

  const embedPreview = isLecturaEmbedPreviewMode();
  if (embedPreview) {
    document.documentElement.classList.add('lectura-next-embed-preview');
    window.addEventListener('message', onEmbedPreviewParentMessage);
  }

  if (!embedPreview) {
    ensureRevealSummaryPrintPdfQuery();
    injectRevealPrintPdfStyles();
  }

  const revealEl = ensureRevealSlides(root);
  ensureSlidesWrap();

  const defaultKb =
    typeof DEFAULT_REVEAL_OPTIONS.keyboard === 'object' && DEFAULT_REVEAL_OPTIONS.keyboard !== null
      ? (DEFAULT_REVEAL_OPTIONS.keyboard as Record<string | number, unknown>)
      : {};
  const revealOptions = {
    ...DEFAULT_REVEAL_OPTIONS,
    ...(userConfig.reveal || {}),
    /* Print-PDF must override e.g. showNotes after user reveal options */
    ...(!embedPreview ? getRevealPrintPdfRevealOptions() : {}),
    ...(embedPreview
      ? {
          controls: false,
          progress: false,
          overview: false,
          slideNumber: false,
          showSlideNumber: false,
          hash: false,
          help: false,
          keyboard: { ...defaultKb, 84: null, 191: null }
        }
      : {})
  };
  const revealJsUrl = userConfig.revealJsUrl || REVEAL_CDN + '/dist/reveal.js';
  const revealCssUrl = userConfig.revealCssUrl || REVEAL_CDN + '/dist/reveal.css';
  const themeUrl = userConfig.themeUrl || REVEAL_CDN + '/dist/theme/white.css';
  const revealJsLocalUrl = userConfig.revealJsUrl || REVEAL_LOCAL + '/dist/reveal.js';
  const revealCssLocalUrl = userConfig.revealCssUrl || REVEAL_LOCAL + '/dist/reveal.css';
  const themeLocalUrl = userConfig.themeUrl || REVEAL_LOCAL + '/dist/theme/white.css';

  const { container, revealJsUrl: _u, revealCssUrl: _c, themeUrl: _t, reveal, ...lecturaConfig } = userConfig;

  function initLectura(): LecturaApi {
    (window as unknown as { Reveal: typeof Reveal }).Reveal = (window as unknown as { Reveal: typeof Reveal }).Reveal || (revealEl as unknown as { reveal?: typeof Reveal }).reveal;
    if (embedPreview) {
      const R = (window as unknown as {
        Reveal?: {
          configure?: (o: Record<string, unknown>) => void;
          sync?: () => void;
        };
      }).Reveal;
      if (R?.configure) {
        R.configure({
          controls: false,
          progress: false,
          overview: false,
          slideNumber: false,
          showSlideNumber: false,
          hash: false,
          help: false,
          keyboard: { ...defaultKb, 84: null, 191: null }
        });
        R.sync?.();
      }
    }
    const lecturaInput = embedPreview
      ? {
          ...lecturaConfig,
          showFab: false,
          features: {
            chat: false,
            notes: false,
            rating: false,
            auth: false,
            profile: false,
            tts: false,
            remote: false,
            resources: false
          }
        }
      : lecturaConfig;
    const hostAdapter = createRevealAdapter();
    const api = init(getConfig(lecturaInput), hostAdapter, {
      drawerOnly: false,
      embedPreview
    });
    if (embedPreview) {
      /**
       * Remote preview iframe: MQTT `TTS_RUN` → postMessage → `applyRemoteV1CommandToReveal` → `LecturaNext.runCodeword`.
       * `registerTTS` needs a `.lectura-bar-tts` mount (orchestrator skips the bar when `embedPreview`); use a hidden slot.
       */
      const ttsMount = document.createElement('div');
      ttsMount.className = 'lectura-bar-tts lectura-embed-preview-tts';
      ttsMount.setAttribute('aria-hidden', 'true');
      ttsMount.style.cssText =
        'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
      document.body.appendChild(ttsMount);
      (window as unknown as { __lecturaTtsContainer?: HTMLElement }).__lecturaTtsContainer = ttsMount;
      const ttsApi = registerTTS(hostAdapter, { ttsSlot: ttsMount });
      if (ttsApi) {
        api.toggleTTS = ttsApi.toggleTTS;
        api.runCodeword = ttsApi.runCodeword;
      }
      const w = window as unknown as {
        RevealNotesTTS?: { init: (r: unknown) => void };
        Reveal?: unknown;
      };
      if (w.RevealNotesTTS?.init && w.Reveal) {
        try {
          w.RevealNotesTTS.init(w.Reveal);
        } catch {
          /* optional — deck may load notes-tts after this; Lectura runCodeword still handles most codewords */
        }
      }
      const R = (window as unknown as { Reveal?: { on?: (ev: string, fn: () => void) => void } }).Reveal;
      R?.on?.('ready', tryApplyEmbedPreviewSlide);
      tryApplyEmbedPreviewSlide();
    }
    (window as unknown as { LecturaNext: LecturaApi }).LecturaNext = api;
    return api;
  }

  const win = window as unknown as { Reveal?: typeof Reveal };
  const RevealGlobal = win.Reveal;
  if (typeof RevealGlobal !== 'undefined' && typeof RevealGlobal.initialize === 'function') {
    return new Promise((resolve) => {
      if (RevealGlobal.isReady && RevealGlobal.isReady()) {
        resolve(initLectura());
      } else {
        RevealGlobal.on!('ready', () => resolve(initLectura()));
      }
    });
  }

  return loadCSS(revealCssUrl)
    .catch(() => loadCSS(revealCssLocalUrl))
    .then(() => loadCSS(themeUrl).catch(() => loadCSS(themeLocalUrl)))
    .then(() =>
      loadCSS(COPYCODE_CDN + '/plugin/copycode/copycode.css').catch(() => loadCSS(COPYCODE_CSS_LOCAL))
    )
    .then(() => loadScript(revealJsUrl).catch(() => loadScript(revealJsLocalUrl)))
    .then(() =>
      Promise.all(
        REVEAL_PLUGIN_PATHS.map((p) =>
          loadScript(REVEAL_CDN + p).catch(() => loadScript(REVEAL_LOCAL + p))
        )
      )
    )
    .then(() =>
      loadScript(COPYCODE_CDN + '/plugin/copycode/copycode.js').catch(() =>
        loadScript(COPYCODE_JS_LOCAL)
      )
    )
    .then(() => {
      const R = win.Reveal;
      if (typeof R === 'undefined') {
        throw new Error('Lectura embedded: Reveal.js did not attach to window');
      }
      R.initialize!({
        ...revealOptions,
        copycode: { button: 'hover', display: 'icons' },
        plugins: getRevealPlugins()
      });
      if (!embedPreview) {
        installRevealPrintHooks(R);
      }
      return new Promise<LecturaApi>((resolve) => {
        if (R.isReady && R.isReady()) {
          resolve(initLectura());
        } else {
          R.on!('ready', () => resolve(initLectura()));
        }
      });
    });
}

if (typeof window !== 'undefined') {
  (window as unknown as { LecturaNextEmbedded: { init: typeof initEmbedded } }).LecturaNextEmbedded = { init: initEmbedded };
}
